import { Component, computed, input } from '@angular/core';
import type { CategorieEquipement } from '../../Services/defauts-materiels';
import { MaterielCarteComponent, type CarteMateriel } from '../materiel-carte/materiel-carte';

const FAMILLES: Record<CategorieEquipement, { libelle: string; icone: string; unite: string }> = {
  supervision: { libelle: 'Supervision et communication', icone: 'dns', unite: 'équipement' },
  onduleurs: { libelle: 'Onduleurs', icone: 'battery_android_bolt', unite: 'onduleur' },
  jonction: { libelle: 'Boîtiers de jonction', icone: 'polyline', unite: 'équipement' },
  comptage: { libelle: 'Comptage', icone: 'speed', unite: 'équipement' },
  capteurs: { libelle: 'Capteurs', icone: 'monitor_heart', unite: 'capteur' },
  infrastructure: { libelle: 'Infrastructure', icone: 'account_tree', unite: 'équipement' },
};

const PRIORITE_ETATS: Readonly<Record<CarteMateriel['etat']['classe'], number>> = {
  production: 0,
  communication: 1,
  normal: 2,
  inconnu: 3,
};
const ORDRE_ALPHABETIQUE = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

/** Défauts de production, communication, puis OK ; les noms départagent chaque état. */
function comparerEquipements(a: CarteMateriel, b: CarteMateriel): number {
  return (
    PRIORITE_ETATS[a.etat.classe] - PRIORITE_ETATS[b.etat.classe] ||
    ORDRE_ALPHABETIQUE.compare(a.libelle, b.libelle) ||
    a.idMateriel - b.idMateriel
  );
}

type GroupeMateriel = {
  cle: string;
  categorie: CategorieEquipement;
  libelle: string;
  icone: string;
  unite: string;
  cartes: CarteMateriel[];
};

/** Un groupe par famille, indépendamment des bâtiments et rampants de l'inventaire. */
@Component({
  selector: 'app-materiel-groupes',
  imports: [MaterielCarteComponent],
  templateUrl: './materiel-groupes.html',
  styleUrl: './materiel-groupes.css',
})
export class MaterielGroupesComponent {
  readonly cartes = input.required<CarteMateriel[]>();
  readonly installationId = input.required<number>();

  protected readonly groupes = computed(() => {
    const installationId = this.installationId();
    const groupes = new Map<string, GroupeMateriel>();
    for (const carte of this.cartes()) {
      // La clé inclut le site pour replier les accordéons lors d'un changement d'installation.
      const cle = `${installationId}-${carte.categorie}`;
      let groupe = groupes.get(cle);
      if (!groupe) {
        const famille = FAMILLES[carte.categorie];
        groupe = {
          ...famille,
          cle,
          categorie: carte.categorie,
          cartes: [],
        };
        groupes.set(cle, groupe);
      }
      groupe.cartes.push(carte);
    }

    const ordre = Object.keys(FAMILLES);
    return [...groupes.values()]
      .sort((a, b) => ordre.indexOf(a.categorie) - ordre.indexOf(b.categorie))
      .map((groupe) => {
        // Compter les matériels, pas les tickets. Un matériel peut cumuler les deux défauts.
        const production = groupe.cartes.filter((c) =>
          c.defautsActifs.includes('production'),
        ).length;
        const communication = groupe.cartes.filter((c) =>
          c.defautsActifs.includes('communication'),
        ).length;
        const inconnu = groupe.cartes.some((c) => c.etat.classe === 'inconnu');
        return {
          ...groupe,
          cartes: [...groupe.cartes].sort(comparerEquipements),
          production,
          communication,
          inconnu,
          classe: inconnu
            ? 'inconnu'
            : production
              ? 'production'
              : communication
                ? 'communication'
                : 'normal',
        };
      });
  });
}
